import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MaintenanceBarrier } from './BarriersSection';
import { formatBarrierDateTime } from './dateTimeUtils';
import { AtaCodeRecord, getChapters, getSections } from '../../services/ataCodeService';
import { MaintenanceDefect, UnscheduledEvent, SkyWrenchResult } from '../../types';
import {
  BARRIER_TYPES,
  BARRIER_TYPE_ABBREVIATIONS,
  DEFECT_HISTORY_LOOKBACK_DAYS,
} from '../../constants';
import { processNoteText } from '../../utils/processNoteText';
import { HistoricalMatch, findRelatedDefectHistory } from './defect-history-matching';
import SkyWrenchModal from '../SkyWrenchModal';
import skyWrenchLogo from '../../assets/skywrench-logo.png';

const NO_RELATED_DEFECTS_TEXT = `No related defects within the past ${DEFECT_HISTORY_LOOKBACK_DAYS} days.`;
const DEFECT_HISTORY_COLLAPSED_LIMIT = 5;

function noRelatedDefectsHistoryEntry(): HistoricalMatch {
  return {
    eventId: 'none',
    erts: '',
    defectText: NO_RELATED_DEFECTS_TEXT,
    correctiveAction: '',
  };
}

function historicalMatchSelectionKey(match: HistoricalMatch, index: number): string {
  return `${match.eventId}-${index}`;
}

function areAllHistoricalMatchesSelected(
  matches: HistoricalMatch[],
  selectedMatches: Record<string, boolean>,
): boolean {
  return (
    matches.length > 1 &&
    !selectedMatches.none &&
    matches.every(
      (match: HistoricalMatch, index: number): boolean =>
        !!selectedMatches[historicalMatchSelectionKey(match, index)],
    )
  );
}

function selectAllHistoricalMatches(
  matches: HistoricalMatch[],
  selectedMatches: Record<string, boolean>,
  checked: boolean,
): Record<string, boolean> {
  const nextSelection: Record<string, boolean> = { ...selectedMatches, none: false };
  matches.forEach((match: HistoricalMatch, index: number): void => {
    nextSelection[historicalMatchSelectionKey(match, index)] = checked;
  });
  return nextSelection;
}

function visibleHistoricalMatches(
  entries: HistoricalMatch[],
  expanded: boolean,
): HistoricalMatch[] {
  const newestFirst: HistoricalMatch[] = [...entries].sort(
    (first: HistoricalMatch, second: HistoricalMatch): number =>
      second.erts.localeCompare(first.erts),
  );
  return entries.length > DEFECT_HISTORY_COLLAPSED_LIMIT && !expanded
    ? newestFirst.slice(0, DEFECT_HISTORY_COLLAPSED_LIMIT)
    : newestFirst;
}

/**
 * Props for the DefectsSection component.
 */
interface DefectsSectionProps {
  defects: MaintenanceDefect[];
  setDefects: React.Dispatch<React.SetStateAction<MaintenanceDefect[]>>;
  mode: 'view' | 'edit' | 'create';
  ataCodes?: AtaCodeRecord[];
  selectedChapters?: Record<number, string>;
  setSelectedChapters?: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  selectedSections?: Record<number, string>;
  setSelectedSections?: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  projectedErts?: Record<number, string>;
  setProjectedErts?: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  /** Hides the Projected ERTS field — not applicable for Planned, Deferred, and Scheduled records. */
  hideProjectedErts?: boolean;
  barriers?: MaintenanceBarrier[];
  defectHistory?: Record<number, HistoricalMatch[]>;
  setDefectHistory?: React.Dispatch<React.SetStateAction<Record<number, HistoricalMatch[]>>>;
  isReadOnly?: boolean;
  completedEvents?: UnscheduledEvent[];
  /** True once the completed-history search corpus is settled rather than still loading. */
  completedHistoryResolved?: boolean;
  /** Incoming event identity; changes when a projected event finishes hydrating. */
  historySourceEvent?: UnscheduledEvent | null;
  tailNumber?: string;
  /** Whether ATA chapter/section are required for this record (gates Create). */
  requiresClassification?: boolean;
  /** Whether at least one defect is required for this record (gates Create). */
  defectsRequired?: boolean;
  /** Whether defect history is required (false for Scheduled records). */
  historyRequired?: boolean;
  skyWrenchResults?: Record<number, SkyWrenchResult>;
  onSkyWrenchTrigger?: (defectId: number, defectText?: string, ataCode?: string) => void;
  /** Triage ATA recommendations awaiting reviewer acknowledgement. */
  unconfirmedAtaRecommendationDefectIds?: number[];
  /** ATA IDs that are mandatory for this record, including newly added manual defects. */
  requiredAtaDefectIds?: number[];
  /** Marks a recommended ATA selection as manually acknowledged. */
  onAtaRecommendationAcknowledged?: (defectId: number) => void;
  /** Starts ATA classification after a reviewer creates a new manual defect. */
  onDefectCreated?: (defectId: number) => void;
  /**
   * Whether the event's carrier is supported by SkyWrench (maps to a knowledge
   * base). When explicitly false, the SkyWrench icon is subdued and disabled.
   * Undefined is treated as supported (no gating).
   */
  skyWrenchSupported?: boolean;
  onEditBarrier?: (barrier: MaintenanceBarrier) => void;
  onDeleteBarrier?: (barrier: MaintenanceBarrier) => void;
  /** Deletes a defect and cascades removal of its associated maintenance barriers. */
  onDeleteDefect?: (defectId: number) => void;
}

/**
 * Calculates open barrier duration in hours.
 */
function barrierDuration(barrier: MaintenanceBarrier): string {
  const start = new Date(
    barrier.startTime.replace(' ', 'T') + (barrier.startTime.includes('Z') ? '' : 'Z'),
  );
  if (isNaN(start.getTime())) return '0.00';
  const diffMs = Date.now() - start.getTime();
  return diffMs > 0 ? (diffMs / (1000 * 60 * 60)).toFixed(2) : '0.00';
}

/**
 * Defects section — each defect displays:
 * 1. Defect text with edit icon at end
 * 2. Projected ERTS (right-aligned calendar/time)
 * 3. ATA Code dropdowns
 * 4. Open barriers for this defect
 * 5. Defect history
 */
const DefectsSection: React.FC<DefectsSectionProps> = ({
  defects,
  setDefects,
  mode,
  ataCodes,
  selectedChapters,
  setSelectedChapters,
  selectedSections,
  setSelectedSections,
  projectedErts,
  hideProjectedErts,
  barriers,
  defectHistory,
  setDefectHistory,
  isReadOnly,
  completedEvents,
  completedHistoryResolved,
  historySourceEvent,
  tailNumber,
  requiresClassification,
  defectsRequired,
  historyRequired,
  skyWrenchResults,
  onSkyWrenchTrigger,
  unconfirmedAtaRecommendationDefectIds,
  requiredAtaDefectIds,
  onAtaRecommendationAcknowledged,
  onDefectCreated,
  skyWrenchSupported,
  onEditBarrier,
  onDeleteBarrier,
  onDeleteDefect,
}) => {
  const [isAddingDefect, setIsAddingDefect] = useState<boolean>(false);
  const [newDefectText, setNewDefectText] = useState<string>('');
  const [editingDefectId, setEditingDefectId] = useState<number | null>(null);
  const [editDefectText, setEditDefectText] = useState<string>('');
  const [historyPopupDefectId, setHistoryPopupDefectId] = useState<number | null>(null);
  const [selectedMatches, setSelectedMatches] = useState<Record<string, boolean>>({});
  const [autoPopulatedHistoryDefectIds, setAutoPopulatedHistoryDefectIds] = useState<number[]>([]);
  const [expandedHistoryDefectIds, setExpandedHistoryDefectIds] = useState<Set<number>>(
    new Set<number>(),
  );
  const [skyWrenchViewId, setSkyWrenchViewId] = useState<number | null>(null);
  const [defectToDelete, setDefectToDelete] = useState<number | null>(null);
  const nextDefectIdRef = useRef<number>(0);

  // A list projection can open before its full event record arrives. Reset the
  // session-only marker when that source record is replaced so saved history is
  // never shown as a new auto-populated value.
  useLayoutEffect((): void => {
    setAutoPopulatedHistoryDefectIds([]);
    setExpandedHistoryDefectIds(new Set<number>());
  }, [historySourceEvent]);

  const toggleDefectHistory = (defectId: number): void => {
    setExpandedHistoryDefectIds((previous: Set<number>): Set<number> => {
      const nextExpandedDefectIds: Set<number> = new Set<number>(previous);
      if (nextExpandedDefectIds.has(defectId)) nextExpandedDefectIds.delete(defectId);
      else nextExpandedDefectIds.add(defectId);
      return nextExpandedDefectIds;
    });
  };

  useEffect((): void => {
    const highestExistingDefectId: number = defects.reduce(
      (highestId: number, defect: MaintenanceDefect): number => Math.max(highestId, defect.id),
      0,
    );
    nextDefectIdRef.current = Math.max(nextDefectIdRef.current, highestExistingDefectId);
  }, [defects]);

  const isEditable = mode === 'create' || mode === 'edit';

  const updateDefectCompletion = (
    defectId: number,
    field: 'defectCompletionDate' | 'defectCompletionTime',
    value: string,
  ): void => {
    setDefects((previous) =>
      previous.map((defect) => (defect.id === defectId ? { ...defect, [field]: value } : defect)),
    );
  };

  const getOpenBarriers = (defectId: number): MaintenanceBarrier[] => {
    if (!barriers) return [];
    return barriers.filter((b) => !b.endTime && b.defectId === defectId);
  };

  /** Returns all barriers (open and closed) associated with a defect. */
  const getDefectBarriers = (defectId: number): MaintenanceBarrier[] => {
    if (!barriers) return [];
    return barriers.filter((b) => b.defectId === defectId);
  };

  /**
   * Sums the total barrier duration (hours) for a defect across all of its
   * barriers. Closed barriers use end - start; open barriers accrue from
   * start to now, matching the running duration shown per open barrier.
   */
  const totalBarrierDuration = (defectId: number): string => {
    let totalMs = 0;
    for (const barrier of getDefectBarriers(defectId)) {
      const start = new Date(
        barrier.startTime.replace(' ', 'T') + (barrier.startTime.includes('Z') ? '' : 'Z'),
      );
      if (isNaN(start.getTime())) continue;
      const end = barrier.endTime
        ? new Date(barrier.endTime.replace(' ', 'T') + (barrier.endTime.includes('Z') ? '' : 'Z'))
        : new Date();
      if (isNaN(end.getTime())) continue;
      const diffMs = end.getTime() - start.getTime();
      if (diffMs > 0) totalMs += diffMs;
    }
    return (totalMs / (1000 * 60 * 60)).toFixed(2);
  };

  /**
   * Sums barrier duration (hours) per barrier type for a defect. Closed
   * barriers use end - start; open barriers accrue from start to now,
   * consistent with totalBarrierDuration.
   */
  const barrierDurationByType = (defectId: number): Record<string, number> => {
    const totals: Record<string, number> = {};
    for (const barrier of getDefectBarriers(defectId)) {
      const start = new Date(
        barrier.startTime.replace(' ', 'T') + (barrier.startTime.includes('Z') ? '' : 'Z'),
      );
      if (isNaN(start.getTime())) continue;
      const end = barrier.endTime
        ? new Date(barrier.endTime.replace(' ', 'T') + (barrier.endTime.includes('Z') ? '' : 'Z'))
        : new Date();
      if (isNaN(end.getTime())) continue;
      const diffMs = end.getTime() - start.getTime();
      if (diffMs > 0)
        totals[barrier.type] = (totals[barrier.type] || 0) + diffMs / (1000 * 60 * 60);
    }
    return totals;
  };

  /** Search completed events for similar defects on this record's tail. */
  const findHistoricalMatches = (defectText: string): HistoricalMatch[] =>
    findRelatedDefectHistory(defectText, completedEvents, tailNumber);

  const hasHistoryCompleted = (defectId: number): boolean => {
    return !!(defectHistory && defectHistory[defectId] && defectHistory[defectId].length > 0);
  };

  useEffect((): void => {
    // Match the picker: a completed corpus with no selectable matches, including
    // a draft before its tail is known, selects the sole no-related-defects option.
    if (!setDefectHistory || !isEditable || !completedHistoryResolved) return;
    const automaticallySelectedDefectIds: number[] = defects
      .filter(
        (defect: MaintenanceDefect): boolean =>
          !defectHistory?.[defect.id]?.length && findHistoricalMatches(defect.text).length === 0,
      )
      .map((defect: MaintenanceDefect): number => defect.id);
    if (automaticallySelectedDefectIds.length === 0) return;
    setDefectHistory(
      (previous: Record<number, HistoricalMatch[]>): Record<number, HistoricalMatch[]> => {
        const nextHistory: Record<number, HistoricalMatch[]> = { ...previous };
        automaticallySelectedDefectIds.forEach((defectId: number): void => {
          if (!nextHistory[defectId]?.length) {
            nextHistory[defectId] = [noRelatedDefectsHistoryEntry()];
          }
        });
        return nextHistory;
      },
    );
    setAutoPopulatedHistoryDefectIds((previous: number[]): number[] =>
      Array.from(new Set<number>([...previous, ...automaticallySelectedDefectIds])),
    );
  }, [
    completedEvents,
    completedHistoryResolved,
    defectHistory,
    defects,
    isEditable,
    setDefectHistory,
    tailNumber,
  ]);

  /**
   * Opens the defect-history selection popup, pre-selecting the entries already
   * saved for this defect so the user can select or deselect related events
   * after the initial creation. Stored entries are matched back to the
   * freshly-computed match list by event id, defect text, and ERTS date; the
   * "no related defects" sentinel maps to the `none` key.
   */
  const openHistoryPopup = (defectId: number, defectText: string): void => {
    const matches = findHistoricalMatches(defectText);
    const existing = (defectHistory && defectHistory[defectId]) || [];
    const initial: Record<string, boolean> = {};
    for (const entry of existing) {
      if (entry.eventId === 'none') {
        initial.none = true;
        continue;
      }
      const idx = matches.findIndex(
        (m) =>
          m.eventId === entry.eventId && m.defectText === entry.defectText && m.erts === entry.erts,
      );
      if (idx >= 0) initial[`${entry.eventId}-${idx}`] = true;
    }
    if (matches.length === 0 && completedHistoryResolved) initial.none = true;
    setSelectedMatches(initial);
    setHistoryPopupDefectId(defectId);
  };

  return (
    <div className="field-row field-row-tall">
      <label className="field-label">
        Defect
        {isEditable && (
          <button
            className={`defect-add-btn${defectsRequired && defects.length === 0 ? ' required-action-missing' : ''}`}
            type="button"
            onClick={() => {
              setIsAddingDefect(true);
              setNewDefectText('');
            }}
            aria-label="Add defect"
          >
            +
          </button>
        )}
      </label>
      <div className="field-group defect-group">
        {defects.map((defect) => (
          <div key={defect.id} className="defect-item">
            {editingDefectId === defect.id ? (
              <div className="defect-edit-row">
                <span className="defect-number">{defect.id})</span>
                <textarea
                  className="field-textarea"
                  value={editDefectText}
                  onChange={(e) => setEditDefectText(e.target.value)}
                  rows={2}
                />
                <button
                  className="defect-save-btn"
                  type="button"
                  onClick={() => {
                    const newText: string = processNoteText(editDefectText.trim());
                    setDefects((prev) =>
                      prev.map((d) => (d.id === defect.id ? { ...d, text: newText } : d)),
                    );
                    // Re-activate defect history when defect is edited
                    if (setDefectHistory)
                      setDefectHistory((prev) => {
                        const copy = { ...prev };
                        delete copy[defect.id];
                        return copy;
                      });
                    setAutoPopulatedHistoryDefectIds((previous: number[]): number[] =>
                      previous.filter((defectId: number): boolean => defectId !== defect.id),
                    );
                    // SkyWrench is (re)generated on Create/Update, not on each defect
                    // edit — a changed defect's stale report is refreshed at save time.
                    setEditingDefectId(null);
                  }}
                >
                  Save
                </button>
                <button
                  className="defect-cancel-btn"
                  type="button"
                  onClick={() => setEditingDefectId(null)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="defect-display-row">
                <span className="defect-number">{defect.id})</span>
                <span className="defect-text">{defect.text}</span>
                {isEditable && (
                  <button
                    className="defect-edit-btn"
                    type="button"
                    onClick={() => {
                      setEditingDefectId(defect.id);
                      setEditDefectText(defect.text);
                    }}
                    aria-label="Edit defect"
                  >
                    &#9998;
                  </button>
                )}
                {isEditable && onDeleteDefect && (
                  <button
                    className="defect-delete-btn"
                    type="button"
                    onClick={() => setDefectToDelete(defect.id)}
                    aria-label="Delete defect"
                    title="Delete defect"
                  >
                    &#128465;
                  </button>
                )}
                {skyWrenchResults !== undefined && (
                  <button
                    className={`defect-edit-btn skywrench-btn ${skyWrenchSupported === false ? 'skywrench-unsupported' : ''} ${skyWrenchResults[defect.id]?.status === 'complete' ? 'skywrench-ready' : ''} ${skyWrenchResults[defect.id]?.status === 'pending' ? 'skywrench-pending' : ''} ${skyWrenchResults[defect.id]?.status === 'error' ? 'skywrench-ready' : ''} ${!skyWrenchResults[defect.id] && isEditable && defect.text && skyWrenchSupported !== false ? 'skywrench-generate' : ''}`}
                    type="button"
                    aria-label="SkyWrench Analysis"
                    title={
                      skyWrenchSupported === false
                        ? 'Aircraft type not currently supported by SkyWrench'
                        : skyWrenchResults[defect.id]?.status === 'complete'
                          ? 'View SkyWrench Analysis'
                          : skyWrenchResults[defect.id]?.status === 'pending'
                            ? 'SkyWrench analysis in progress...'
                            : skyWrenchResults[defect.id]?.status === 'error'
                              ? 'SkyWrench analysis failed — click to view and re-run'
                              : 'Generate SkyWrench analysis'
                    }
                    disabled={
                      skyWrenchSupported === false ||
                      skyWrenchResults[defect.id]?.status === 'pending' ||
                      (!skyWrenchResults[defect.id] && (!isEditable || !defect.text))
                    }
                    onClick={() => {
                      if (skyWrenchSupported === false) return;
                      const result = skyWrenchResults[defect.id];
                      if (result?.status === 'pending') return;
                      // A finished run — complete OR error — opens the modal,
                      // where the output (or the error message) is shown and can
                      // be re-run via the header control. This gives the user a
                      // visible, actionable path instead of an invisible retry.
                      if (result) {
                        setSkyWrenchViewId(defect.id);
                        return;
                      }
                      // No run yet: generate directly from the row (edit only).
                      if (isEditable && onSkyWrenchTrigger && defect.text) {
                        onSkyWrenchTrigger(defect.id, defect.text);
                      }
                    }}
                  >
                    <img
                      className="skywrench-btn-logo"
                      src={skyWrenchLogo}
                      alt=""
                      aria-hidden="true"
                    />
                  </button>
                )}
                {/* Projected ERTS — right-aligned, system-generated (ERTS Prediction Pipeline). */}
                {/* Hidden for Planned, Deferred, and Scheduled records where it is not applicable. */}
                {!hideProjectedErts && (
                  <div className="defect-projected-erts">
                    <span className="field-input-label">Projected ERTS</span>
                    <input
                      type="date"
                      className="field-input defect-erts-date"
                      value={
                        projectedErts && projectedErts[defect.id]
                          ? projectedErts[defect.id].split(' ')[0] ||
                            projectedErts[defect.id].split('T')[0] ||
                            ''
                          : ''
                      }
                      readOnly
                      disabled
                      title="Projected by the ERTS Prediction Pipeline"
                    />
                    <input
                      type="text"
                      className="field-input defect-erts-time"
                      value={(() => {
                        const v = (projectedErts && projectedErts[defect.id]) || '';
                        const parts = v.split(/[ T]/);
                        return parts[1] ? parts[1].substring(0, 5) : '';
                      })()}
                      readOnly
                      disabled
                      placeholder="HH:MM"
                      maxLength={5}
                      title="Projected by the ERTS Prediction Pipeline"
                    />
                  </div>
                )}
              </div>
            )}

            {/* ATA Code and defect completion — one row below Projected ERTS. */}
            <div className="defect-ata-row">
              <span className="defect-sub-label">ATA Code</span>
              <select
                className={`field-input defect-ata-select${(requiresClassification || requiredAtaDefectIds?.includes(defect.id)) && !(selectedChapters && selectedChapters[defect.id]) ? ' field-required-missing' : ''}${unconfirmedAtaRecommendationDefectIds?.includes(defect.id) ? ' ata-recommendation-pending' : ''}`}
                value={(selectedChapters && selectedChapters[defect.id]) || ''}
                disabled={!!isReadOnly}
                onChange={(e) => {
                  if (setSelectedChapters)
                    setSelectedChapters((prev) => ({ ...prev, [defect.id]: e.target.value }));
                  if (setSelectedSections)
                    setSelectedSections((prev) => ({ ...prev, [defect.id]: '' }));
                  onAtaRecommendationAcknowledged?.(defect.id);
                }}
              >
                <option value="">Chapter...</option>
                {ataCodes &&
                  getChapters(ataCodes).map((ch) => (
                    <option key={ch.chapter} value={ch.chapter}>
                      {ch.label}
                    </option>
                  ))}
              </select>
              <select
                className={`field-input defect-ata-select${(requiresClassification || requiredAtaDefectIds?.includes(defect.id)) && selectedChapters && selectedChapters[defect.id] && !(selectedSections && selectedSections[defect.id]) ? ' field-required-missing' : ''}${unconfirmedAtaRecommendationDefectIds?.includes(defect.id) ? ' ata-recommendation-pending' : ''}`}
                value={(selectedSections && selectedSections[defect.id]) || ''}
                disabled={!!isReadOnly || !(selectedChapters && selectedChapters[defect.id])}
                onChange={(e) => {
                  const section = e.target.value;
                  if (setSelectedSections)
                    setSelectedSections((prev) => ({ ...prev, [defect.id]: section }));
                  onAtaRecommendationAcknowledged?.(defect.id);
                  // ATA chapter/section changes do NOT re-run SkyWrench — the
                  // analysis is keyed off the defect text, not the ATA code.
                }}
              >
                <option value="">Section...</option>
                {ataCodes &&
                  selectedChapters &&
                  selectedChapters[defect.id] &&
                  getSections(ataCodes, selectedChapters[defect.id]).map((sec) => (
                    <option key={sec.ataCodeId} value={sec.section}>
                      {sec.label}
                    </option>
                  ))}
              </select>
              {unconfirmedAtaRecommendationDefectIds?.includes(defect.id) && !isReadOnly && (
                <button
                  className="defect-edit-btn ata-recommendation-confirm-btn"
                  type="button"
                  onClick={() => onAtaRecommendationAcknowledged?.(defect.id)}
                  aria-label={`Confirm ATA recommendation for defect ${defect.id}`}
                  title="Confirm Knowledge Base ATA recommendation"
                >
                  ✓
                </button>
              )}
              <span className="field-input-label">Defect Completion</span>
              <input
                type="date"
                className="field-input defect-erts-date"
                value={defect.defectCompletionDate || ''}
                disabled={!isEditable || !!isReadOnly}
                aria-label={`Defect ${defect.id} completion date`}
                onChange={(event) =>
                  updateDefectCompletion(defect.id, 'defectCompletionDate', event.target.value)
                }
              />
              <input
                type="text"
                className="field-input defect-erts-time"
                value={defect.defectCompletionTime || ''}
                disabled={!isEditable || !!isReadOnly}
                placeholder="HH:MM"
                maxLength={5}
                aria-label={`Defect ${defect.id} completion time`}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                  const digits: string = event.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                  const formattedTime: string =
                    digits.length >= 3 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
                  updateDefectCompletion(defect.id, 'defectCompletionTime', formattedTime);
                }}
              />
            </div>

            {/* Open barriers for this defect */}
            {getOpenBarriers(defect.id).length > 0 && (
              <div className="defect-barriers">
                <span className="defect-sub-label">Open Barriers</span>
                {getOpenBarriers(defect.id).map((b) => (
                  <div key={b.id} className="defect-barrier-entry">
                    <span className="defect-barrier-type">{b.type}</span>
                    <span className="defect-barrier-start">
                      {formatBarrierDateTime(b.startTime)}
                    </span>
                    <span className="defect-barrier-duration">{barrierDuration(b)}h</span>
                    {b.notes && <span className="defect-barrier-notes">{b.notes}</span>}
                    {isEditable && onEditBarrier && (
                      <button
                        className="barrier-edit-btn"
                        type="button"
                        onClick={() => onEditBarrier(b)}
                        aria-label="Edit barrier"
                      >
                        &#9998;
                      </button>
                    )}
                    {isEditable && onDeleteBarrier && (
                      <button
                        className="barrier-delete-btn"
                        type="button"
                        onClick={() => onDeleteBarrier(b)}
                        aria-label="Delete barrier"
                        title="Delete barrier"
                      >
                        &#128465;
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Defect History */}
            <div
              className={`defect-history${autoPopulatedHistoryDefectIds.includes(defect.id) ? ' defect-history-auto-populated' : ''}`}
              title={
                autoPopulatedHistoryDefectIds.includes(defect.id)
                  ? `Auto-populated because no related defects were found within the past ${DEFECT_HISTORY_LOOKBACK_DAYS} days`
                  : undefined
              }
            >
              <div className="defect-history-header">
                {(defectHistory?.[defect.id]?.length || 0) > DEFECT_HISTORY_COLLAPSED_LIMIT && (
                  <button
                    className="notes-collapse-toggle"
                    type="button"
                    onClick={(): void => toggleDefectHistory(defect.id)}
                    aria-label={
                      expandedHistoryDefectIds.has(defect.id)
                        ? `Show only the 5 most recent defect history entries for defect ${defect.id}`
                        : `Show all defect history entries for defect ${defect.id}`
                    }
                    aria-expanded={expandedHistoryDefectIds.has(defect.id)}
                    title={
                      expandedHistoryDefectIds.has(defect.id)
                        ? 'Collapse to 5 most recent'
                        : 'Show all defect history'
                    }
                  >
                    <span
                      className={`toggle-arrow ${expandedHistoryDefectIds.has(defect.id) ? 'open' : ''}`}
                    >
                      &#9656;
                    </span>
                  </button>
                )}
                <span className="defect-sub-label">Defect History</span>
                {isEditable && (
                  <button
                    className={`defect-edit-btn${historyRequired && !hasHistoryCompleted(defect.id) ? ' required-action-missing' : ''}`}
                    type="button"
                    aria-label="Edit defect history"
                    title="Select or deselect related events"
                    onClick={() => openHistoryPopup(defect.id, defect.text)}
                  >
                    &#9998;
                  </button>
                )}
              </div>
              <div className="defect-history-content">
                {defectHistory &&
                defectHistory[defect.id] &&
                defectHistory[defect.id].length > 0 ? (
                  visibleHistoricalMatches(
                    defectHistory[defect.id],
                    expandedHistoryDefectIds.has(defect.id),
                  ).map((entry: HistoricalMatch, index: number) => (
                    <div
                      key={`${entry.eventId}-${entry.erts}-${index}`}
                      className="defect-history-entry"
                    >
                      {entry.eventId === 'none' ? (
                        <span>{entry.defectText}</span>
                      ) : (
                        <>
                          <div className="history-erts">ERTS: {entry.erts || '—'}</div>
                          <div className="history-defect">{entry.defectText}</div>
                          <div className="history-ca">CA: {entry.correctiveAction || '—'}</div>
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <span className="defect-empty">No history</span>
                )}
              </div>
            </div>

            {/* Total barrier duration for this defect (open + closed) */}
            {getDefectBarriers(defect.id).length > 0 && (
              <div className="defect-barrier-total">
                <span className="defect-sub-label">Total Barrier Duration</span>
                <span className="defect-barrier-total-hours">
                  {totalBarrierDuration(defect.id)}h
                </span>
              </div>
            )}

            {/* Per-barrier-type breakdown for this defect */}
            {getDefectBarriers(defect.id).length > 0 &&
              (() => {
                const totalsByType = barrierDurationByType(defect.id);
                return (
                  <div className="defect-barrier-breakdown">
                    {BARRIER_TYPES.filter((type) => totalsByType[type] !== undefined).map(
                      (type) => (
                        <span key={type} className="defect-barrier-breakdown-item">
                          <span className="defect-barrier-breakdown-abbr">
                            {BARRIER_TYPE_ABBREVIATIONS[type]}
                          </span>
                          <span className="defect-barrier-breakdown-hours">
                            {totalsByType[type].toFixed(2)}h
                          </span>
                        </span>
                      ),
                    )}
                  </div>
                );
              })()}
          </div>
        ))}

        {isAddingDefect && (
          <div className="defect-new-row">
            <span className="defect-number">{defects.length + 1})</span>
            <textarea
              className="field-textarea"
              value={newDefectText}
              onChange={(e) => setNewDefectText(e.target.value)}
              rows={2}
              placeholder="Enter defect description"
              autoFocus
            />
            <button
              className="defect-create-btn"
              type="button"
              disabled={!newDefectText.trim()}
              onClick={() => {
                const highestExistingDefectId: number = defects.reduce(
                  (highestId: number, defect: MaintenanceDefect): number =>
                    Math.max(highestId, defect.id),
                  0,
                );
                const newId: number =
                  Math.max(nextDefectIdRef.current, highestExistingDefectId) + 1;
                nextDefectIdRef.current = newId;
                const processedText: string = processNoteText(newDefectText.trim());
                setDefects((prev) => [
                  ...prev,
                  {
                    id: newId,
                    text: processedText,
                    defectCompletionDate: '',
                    defectCompletionTime: '',
                  },
                ]);
                onDefectCreated?.(newId);
                // SkyWrench analysis is generated on Create/Update (see App save
                // flow), not immediately when the defect is added.
                setNewDefectText('');
                setIsAddingDefect(false);
              }}
            >
              Create Defect
            </button>
            <button
              className="defect-cancel-btn"
              type="button"
              onClick={() => {
                setIsAddingDefect(false);
                setNewDefectText('');
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {defects.length === 0 && !isAddingDefect && (
          <span className="defect-empty">No defects recorded</span>
        )}
      </div>

      {/* Historical Defect Search Popup */}
      {historyPopupDefectId !== null &&
        (() => {
          const defect = defects.find((d) => d.id === historyPopupDefectId);
          const matches = defect ? findHistoricalMatches(defect.text) : [];
          return (
            <div className="barrier-popup-overlay">
              <div
                className="barrier-popup"
                style={{ maxWidth: '42.857rem' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="barrier-popup-header">
                  <h3>Defect History — Defect {historyPopupDefectId}</h3>
                  <button
                    className="modal-close"
                    type="button"
                    onClick={() => setHistoryPopupDefectId(null)}
                  >
                    &times;
                  </button>
                </div>
                <div
                  className="barrier-popup-body"
                  style={{ maxHeight: '28.571rem', overflowY: 'auto' }}
                >
                  {matches.length > 1 && (
                    <label className="history-checkbox-row">
                      <input
                        type="checkbox"
                        checked={areAllHistoricalMatchesSelected(matches, selectedMatches)}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                          const checked: boolean = event.target.checked;
                          setSelectedMatches(
                            (previous: Record<string, boolean>): Record<string, boolean> =>
                              selectAllHistoricalMatches(matches, previous, checked),
                          );
                        }}
                      />
                      <span>Select All</span>
                    </label>
                  )}
                  <label className="history-checkbox-row">
                    <input
                      type="checkbox"
                      checked={!!selectedMatches['none']}
                      onChange={(e) =>
                        setSelectedMatches((prev) => ({ ...prev, none: e.target.checked }))
                      }
                    />
                    <span>
                      No related defects within the past {DEFECT_HISTORY_LOOKBACK_DAYS} days.
                    </span>
                  </label>
                  {matches.map((m, idx) => (
                    <label key={idx} className="history-checkbox-row">
                      <input
                        type="checkbox"
                        checked={!!selectedMatches[historicalMatchSelectionKey(m, idx)]}
                        onChange={(e) =>
                          setSelectedMatches((prev) => ({
                            ...prev,
                            [historicalMatchSelectionKey(m, idx)]: e.target.checked,
                          }))
                        }
                      />
                      <div className="history-match-detail">
                        <div className="history-match-defect">{m.defectText}</div>
                        <div className="history-match-meta">
                          ERTS: {m.erts || '—'} | CA: {m.correctiveAction || '—'}
                        </div>
                      </div>
                    </label>
                  ))}
                  {matches.length === 0 && (
                    <div className="defect-empty" style={{ padding: '0.5714rem 0' }}>
                      No similar defects found in completed records.
                    </div>
                  )}
                </div>
                <div className="barrier-popup-footer">
                  <button
                    className="modal-btn modal-btn-cancel"
                    type="button"
                    onClick={() => setHistoryPopupDefectId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-btn modal-btn-save"
                    type="button"
                    onClick={() => {
                      if (setDefectHistory && historyPopupDefectId !== null) {
                        const entries: HistoricalMatch[] = [];
                        if (selectedMatches['none']) {
                          entries.push(noRelatedDefectsHistoryEntry());
                        }
                        const defect = defects.find((d) => d.id === historyPopupDefectId);
                        const allMatches = defect ? findHistoricalMatches(defect.text) : [];
                        allMatches.forEach((m, idx) => {
                          if (selectedMatches[`${m.eventId}-${idx}`]) entries.push(m);
                        });
                        // Persist the new selection. When nothing is selected, clear
                        // the defect's history so a deselect-all takes effect (and
                        // re-triggers the required-history highlight where applicable).
                        setDefectHistory((prev) => {
                          const copy = { ...prev };
                          if (entries.length > 0) copy[historyPopupDefectId] = entries;
                          else delete copy[historyPopupDefectId];
                          return copy;
                        });
                        setAutoPopulatedHistoryDefectIds((previous: number[]): number[] =>
                          previous.filter(
                            (defectId: number): boolean => defectId !== historyPopupDefectId,
                          ),
                        );
                      }
                      setHistoryPopupDefectId(null);
                    }}
                  >
                    Accept
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Delete Defect Confirmation Popup */}
      {defectToDelete !== null &&
        (() => {
          const barrierCount = getDefectBarriers(defectToDelete).length;
          return (
            <div className="barrier-popup-overlay">
              <div
                className="barrier-popup"
                style={{ maxWidth: '31.4286rem' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="barrier-popup-header">
                  <h3>Delete Defect {defectToDelete}</h3>
                </div>
                <div className="barrier-popup-body">
                  <p>
                    Are you sure you want to delete this defect
                    {barrierCount > 0
                      ? ` and its ${barrierCount} associated maintenance ${barrierCount === 1 ? 'barrier' : 'barriers'}`
                      : ''}
                    ? This action cannot be undone.
                  </p>
                </div>
                <div className="barrier-popup-footer">
                  <button
                    className="modal-btn modal-btn-cancel"
                    type="button"
                    onClick={() => setDefectToDelete(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-btn modal-btn-save"
                    type="button"
                    onClick={() => {
                      if (onDeleteDefect) onDeleteDefect(defectToDelete);
                      setDefectToDelete(null);
                    }}
                  >
                    Accept
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* SkyWrench Results Modal */}
      {skyWrenchViewId !== null &&
        skyWrenchResults &&
        skyWrenchResults[skyWrenchViewId] &&
        (() => {
          const viewId = skyWrenchViewId;
          const viewResult = skyWrenchResults[viewId];
          return (
            <SkyWrenchModal
              result={viewResult}
              isOpen={true}
              onClose={() => setSkyWrenchViewId(null)}
              onRegenerate={
                // Available whenever SkyWrench applies to the record (i.e. not
                // Scheduled, where onSkyWrenchTrigger is undefined) — including
                // view mode (completed or locked-by-another events). Re-running
                // persists through SkyWrench's own targeted endpoint, so it does
                // not depend on holding the edit lock.
                onSkyWrenchTrigger
                  ? () => {
                      // Force a fresh run even when the report is already
                      // "complete" (e.g. it came back with error sections) or in
                      // an "error" state, then close so the defect row reflects
                      // the new pending run. Prefer the defect's current text.
                      const currentText =
                        defects.find((d) => d.id === viewId)?.text || viewResult.defectDescription;
                      onSkyWrenchTrigger(viewId, currentText);
                      setSkyWrenchViewId(null);
                    }
                  : undefined
              }
            />
          );
        })()}
    </div>
  );
};

export default DefectsSection;
